import {
    ICredentialsStore,
    ICredentialsWithServer,
    ICredentials,
    DriveCredentialsStore,
    SandboxedCredentialsStore
} from "./credentialsStore";
import { ICustomHeaders, Transport, XHRTransport } from "./transport";
import { sanitizeUserName, UserService } from "./users";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { log, Source } from "./logger";
import constants from "../constants";
import { getServerDate } from "./storage/date";
import { CancelablePromise as Promise } from "./cancelablePromise";
import { TApiSuccessCallback, TApiErrorCallback, IApiError, TApiCredentialsCallback, IApiCall, TMultiCallParam } from "./api.interfaces";
import { DriveStore } from "../store/reducers";
import * as logger from "../utils/logger";
import { FuzzingBackOffGenerator } from "./fuzzing";
import { ERROR_MESSAGES } from "./errorHandler/error.messages";
import { ERROR_TYPES, isOverLimitException, isServerUnavailableException } from "./errorHandler/error.types";
import { AuthFailureStrategyBuilder } from "./authFailureStrategy";
import { IIamSettings } from "./auth/iamSettings";
import { FeatureFlags, IFeatureFlagsService } from "../services/featureFlags";
import { TContextSource } from "../app.manager";

const DATABASEEXISTS_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const NOOP = () => {
    // nothing to do;
};

export interface IGeotabApiOptions {
    // Use localStorage to store credentials automatically
    rememberMe?: boolean;

    // Show debugging information (in Chrome dev tools, Firebug, etc.)
    debug?: boolean;

    // How long to wait for a response from the server (in seconds); 0 (or null) means no timeout.
    timeout?: number;

    // Disable SSL verification for testing (not recommended)
    disableSsl?: boolean;
}

interface ICallBackoffDictionaryProperties {
    retryDateTime: Date;
    fuzzingGenerator?: FuzzingBackOffGenerator;
    errorCode?: number;
}

interface ICallBackoffDictionary {
    [apiName: string]: ICallBackoffDictionaryProperties;
}

export type TGetCredentialsCallback = (errorMessage?: string, userName?: string) => void;

export class BaseGeotabApi {
    private readonly credentialsStore: ICredentialsStore;
    private readonly featureFlagService: FeatureFlags | undefined;

    private readonly getCredentialsCallback: TGetCredentialsCallback;
    private readonly options: IGeotabApiOptions = {
        rememberMe: true,
        debug: false,
        timeout: 0,
        disableSsl: false
    };
    private credentials;
    private changeNetworkStatus;
    private transport: Transport;
    private getSessionCallback: any = null;
    private backOffCallDictionary: ICallBackoffDictionary = {};
    private readonly authFailureStrategyBuilder: AuthFailureStrategyBuilder;

    constructor(
        getCredentialsCallback: TGetCredentialsCallback,
        newOptions: IGeotabApiOptions,
        customCredentialsStore: ICredentialsStore,
        transport?: Transport,
        store?: DriveStore
    ) {
        if (newOptions) {
            for (let prop in newOptions) {
                if (newOptions.hasOwnProperty(prop)) {
                    this.options[prop] = newOptions[prop];
                }
            }
        }

        this.credentialsStore = customCredentialsStore;
        this.featureFlagService = store && new FeatureFlags(store);
        this.transport = transport || new XHRTransport(window.location.host);

        this.options.timeout && this.transport.setTimeout(this.options.timeout);
        this.options.debug && this.transport.setIsDebug(this.options.debug);
        this.options.disableSsl && this.transport.setIsSslDisabled(this.options.disableSsl);

        this.getCredentialsCallback = getCredentialsCallback;
        this.authFailureStrategyBuilder = new AuthFailureStrategyBuilder({
            credentialsStore: this.credentialsStore,
            featureFlagService: this.featureFlagService,
            clearInMemoryCredentials: () => { this.credentials = null; },
            forget: () => this.forget()
        });
        dispatcher.on(DispatcherEvents.ClockDrift, this.clearBackOffCallDictionary.bind(this), this);
    }

    set isApiLoggingEnabled(_: boolean) {
        this.transport.isLoggingEnabled = false;
    }

    /* Used to make API calls without modifying the app state, such as storing the credentials used in the calls. */
    static fetchWithoutSideEffects = <T>(server: string, methodName: string, params: any, ignoreCredentials?: boolean, binaryData?: ArrayBuffer, credentials?: ICredentials): Promise<T> => {
        const tempTransport = new XHRTransport(server);
        const tempCredentialsStore = new SandboxedCredentialsStore();
        credentials && tempCredentialsStore.set(credentials, server);
        const tempApi = new BaseGeotabApi(() => {}, {}, tempCredentialsStore, tempTransport);
        return new Promise<T>((resolve, reject) => tempApi.call(methodName, params, resolve, reject, ignoreCredentials, binaryData))
            .then(data => {
                tempApi.destroy();
                return data;
            });
    };

    getBackOffCallDictionary() {
        return this.backOffCallDictionary;
    }

    clearBackOffCallDictionary() {
        // Times are inaccurate, need to clear
        this.backOffCallDictionary = {};
    }

    setTransport(transport: Transport) {
        let isOnline = this.transport.getIsOnline();
        this.transport = transport;
        this.transport.setServer(window.location.host);
        this.transport.changeIsOnline(isOnline);
        this.changeNetworkStatus && this.transport.onChangeNetworkStatus(this.changeNetworkStatus);
    }

    setHeaders(headers: ICustomHeaders[]) {
        this.transport.setHeaders(headers);
    }

    getXHRError(xhr: XMLHttpRequest) {
        return (this.transport as XHRTransport).getXHRError(xhr);
    }

    onChangeNetworkStatus(changeNetworkStatusCallback) {
        this.changeNetworkStatus = changeNetworkStatusCallback;
        this.transport.onChangeNetworkStatus(this.changeNetworkStatus);
    }

    authenticate(newServer, database, username, password, success, error) {
        return this.authenticateWithServer(newServer, database, username, password, () => {
            success && success();
            this.getSessionCallback && this.getSessionCallback();
        }, error);
    }

    /**
     *  Calls a Geotab method. Handles cases where the credentials have expired or are invalid.
     *  @param method The name of the API method
     *  @param params The method parameters object.
     *  @param successCallback The function that is called if the method call was successful
     *  @param errorCallback The function that is called if the method call failed
     *  @param ignoreCredentials Allow sending call without credentials (Translate for example)
     *  @return An object with operations for the call. Supported operation(s): abort()
     */
    call(
        method: string,
        params: any,
        successCallback: TApiSuccessCallback,
        errorCallback?: TApiErrorCallback,
        ignoreCredentials = false,
        binaryData?: ArrayBuffer,
        binaryMimeType?: string
    ) {
        let needsLoginAndCall = (errorMessage?: string, username?: string, isNotAuthenticated?: boolean) => {
            let storedCredentials = this.credentialsStore.get();
            if (storedCredentials && storedCredentials.credentials && storedCredentials.server && this.options.rememberMe) {
                this.credentials = storedCredentials.credentials;
                this.transport.setServer(storedCredentials.server);
                this.call(method, params, successCallback, errorCallback, false, undefined);
            } else {
                if (errorMessage && errorMessage.length) {
                    this.forget(null, errorMessage.replace(/[@\\\d]|\bu/g, "").replace("  ", " "), username);
                } else {
                    this.askCredentials();
                }
                if (isNotAuthenticated) {
                    let error = { message: `Failed method: ${ method }`, code: -1, data: { id: "", type: ERROR_TYPES.INVALID_USER, requestIndex: -1, info: "" }};
                    errorCallback && errorCallback(error);
                }
            }
        };

        if (!this.credentials && !ignoreCredentials) {
            needsLoginAndCall(undefined, undefined, true);
            return { abort: constants.NOOP };
        }

        // Don't need to add credentials when we're using iam since it's in the header
        if (!this.credentials?.iamJwt) {
            if (!ignoreCredentials) {
                params.credentials = this.credentials;
            } else {
                let credentials = this.credentialsStore.get();
                if (credentials?.credentials?.database && !credentials.credentials.iamJwt) {
                    params.credentials = params.credentials || {};
                    params.credentials.database = credentials.credentials.database;
                }
            }
        }

        let typeName = "";
        let methodName = "";
        if (params.typeName) {
            typeName = params.typeName;
        } else if (params?.calls?.length > 0) {
            let call = params.calls[0];
            if (call.params) {
                typeName = call.params.typeName || "";
                if (call.method) {
                    methodName = `${ call.method }${ typeName ? ":" : "" }`;
                }
            }
        }

        const sentSessionId = params.credentials?.sessionId;
        let failedCallKey = `${ method }${ methodName || typeName ? ":" : "" }${ methodName }${ typeName }`;
        let existingRateLimit = this.backOffCallDictionary[failedCallKey];
        let nextRetryTime = existingRateLimit ? this.backOffCallDictionary[failedCallKey].retryDateTime : null;

        if (nextRetryTime && new Date().getTime() < nextRetryTime.getTime()) {
            let isServerDownRateLimiting = !!existingRateLimit.fuzzingGenerator;
            let errorCode = this.backOffCallDictionary[failedCallKey].errorCode || -1;
            let messageType = "";
            let exceptionType = "";

            if (isServerDownRateLimiting) {
                messageType = ERROR_MESSAGES.SERVER_UNAVAILABLE;
                exceptionType = ERROR_TYPES.DB_UNAVAILABLE;
            } else {
                messageType = ERROR_MESSAGES.OVERLIMIT;
                exceptionType = ERROR_TYPES.OVERLIMIT;
            }

            if (errorCode >= 500) {
                exceptionType = ERROR_TYPES.NETWORK_ERROR;
            }

            let message = `${ messageType }${ failedCallKey }`;
            if (errorCallback) {
                errorCallback({ message: message, code: errorCode, data: { id: "", type: exceptionType, requestIndex: -1, info: "" }});
                return;
            }
            throw new Error(message);
        }
        let apiCall: IApiCall | null;
        /* eslint-disable complexity */
        apiCall = this.callBase(method, params, (result) => {
            apiCall = null;
            if (this.backOffCallDictionary.hasOwnProperty(failedCallKey)) {
                logger.event(logger.Event.RATELIMITING, `Rate limiting disabled for ${ failedCallKey }.`);
                delete this.backOffCallDictionary[failedCallKey];
            }
            successCallback(result);
        }, (error) => {
            apiCall = null;
            if ((isServerUnavailableException(error) || error.code >= 500) && method !== "DatabaseExists") {
                let fuzzingGenerator = existingRateLimit && existingRateLimit.fuzzingGenerator || new FuzzingBackOffGenerator();
                let backoffDictionaryProperties: ICallBackoffDictionaryProperties = {
                    retryDateTime: new Date(new Date().getTime() + fuzzingGenerator.next()),
                    fuzzingGenerator,
                    errorCode: error.code
                };
                logger.event(
                    logger.Event.RATELIMITING,
                    `Rate limiting enabled for ${ failedCallKey }. Reason: ${ error?.message || "Unknown" }. Code: ${ error?.code }. Ends: ${ backoffDictionaryProperties.retryDateTime.toISOString() }`
                );
                this.backOffCallDictionary[failedCallKey] = backoffDictionaryProperties;
            } else if (isOverLimitException(error)) {
                let retryDelay = error.data?.retry !== undefined ? error.data?.retry : 60; // 1 min retry by default if no retry given.
                let nextRunTime = new Date(new Date().getTime() + (retryDelay * 1000));
                logger.event(logger.Event.RATELIMITING, `Rate limiting enabled for ${ failedCallKey }. Reason: Overlimit exception. Ends: ${ nextRunTime.toISOString() }`);
                this.backOffCallDictionary[failedCallKey] = { retryDateTime: nextRunTime };
            }

            // a session that replaced this call's while it was in flight, like after a password change, did not fail
            const currentSessionId = this.credentialsStore.get()?.credentials?.sessionId;
            if (sentSessionId && currentSessionId && currentSessionId !== sentSessionId) {
                errorCallback?.(error);
                return;
            }

            this.authFailureStrategyBuilder.build(error, method)(errorCallback, needsLoginAndCall);
        }, binaryData, ignoreCredentials, binaryMimeType);
        /* eslint-enable complexity */
        return apiCall;
    }

    /**
     *  Calls multiple Geotab methods at the same time. Returns an array of results corresponding to the order the calls are passed in
     *  @param calls The calls array. For each entity in this array, we expect a 1 or 2 item array: the first
     *                                               item is the method name, and (optionally) the second is an object of the method parameters
     *                                               eg. [
     *                                                      ["GetVersion"],
     *                                                      ["Get", { typeName: "Device", search: { serialNumber: "GTA1234556678" } }],
     *                                                      ["Add", { typeName: "Device", entity: { name: "New Device", serialNumber: "GTA0000000000" }]
     *                                                   ]
     *  @param callbackSuccess The function that is called if the method calls were all successful
     *  @param callbackError The function that is called if any method call failed
     *  @return An object with operations for the call. Supported operation(s): abort()
     */
    multiCall(calls: TMultiCallParam[], callbackSuccess, callbackError?, ignoreCredentials = false): any {
        let formattedCalls = calls.map(call => {
            return {
                method: call[0],
                params: call[1]
            };
        });

        return this.call("ExecuteMultiCall", {
            calls: formattedCalls
        }, callbackSuccess, callbackError, ignoreCredentials, undefined);
    }

    /**
     *  Retrieves a session. Useful for single sign-on or other cases where you require the credentials
     *  @param callbackSuccess The function that is called if the retrieval of sessionId was successful
     *  @param newSession If true, always retrieve a new session from the server. Otherwise, return the current session (if active) or
     *                              retrieve a new one from the server if there are no active sessions
     */
    getSession(newSession = false, ignoreApiNeedsCredentials = false) {
        return new Promise<ICredentialsWithServer>((resolve) => {
            let storedCredentials = this.credentialsStore.get();
            if (!newSession && storedCredentials && this.options.rememberMe) {
                let credentials = storedCredentials.credentials;
                let server = storedCredentials.server;
                let iamSettings = storedCredentials.iamSettings;
                resolve({credentials, server, iamSettings});
            } else {
                this.getSessionCallback = () => {
                    let credentials = this.credentialsStore.get() as ICredentialsWithServer;
                    let server = this.transport.getServer();
                    resolve({credentials: this.credentials, server, iamSettings: credentials?.iamSettings});
                    this.getSessionCallback = null;
                };

                if (!ignoreApiNeedsCredentials) {
                    this.askCredentials();
                }
            }
        });
    }

    refreshSession(): void {
        let storedCredentials = this.credentialsStore.get();
        if (storedCredentials) {
            this.credentials = storedCredentials.credentials;
            storedCredentials.server && this.transport.setServer(storedCredentials.server);
        }
    }

    forget(successCallback?, errorMessage?: string, username?: string, skipNeedsCredentials = false): void {
        this.credentials = null;
        this.credentialsStore.clear();
        !skipNeedsCredentials && this.askCredentials(successCallback || null, errorMessage, username);
    }

    destroy() {
        dispatcher.off(DispatcherEvents.ClockDrift, undefined, this);
    }

    private callBase(
        method: string,
        params: any,
        callbackSuccess: TApiSuccessCallback,
        callbackError: TApiErrorCallback,
        binaryData?: ArrayBuffer,
        ignoreCredentials = false,
        binaryMimeType?: string
    ): IApiCall {
        return this.transport.call(method, params, callbackSuccess, callbackError, binaryData, ignoreCredentials, binaryMimeType);
    }

    private authenticateWithServer(newServer: string, database: string, username: string, password: string, callbackSuccess: TApiSuccessCallback, callbackError: TApiErrorCallback) {
        this.transport.setServer(newServer);
        this.transport.setIsSslDisabled(this.options.disableSsl || !!(window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_SSL));

        return this.callBase("Authenticate", {
            database,
            userName: username,
            password
        }, data => {
            if (data.path && data.path !== "ThisServer") {
                this.transport.setServer(`${ (this.options.disableSsl ? "http://" : "https://") }${ data.path }/`);
            }

            this.credentials = data.credentials;
            this.options.rememberMe && this.credentialsStore.set(this.credentials, this.transport.getServer());
            callbackSuccess && callbackSuccess(this.credentials);
        }, callbackError);
    }

    /**
     * Populates the authenticate callback with a function
     * @param _ Callback on success (not used)
     * @param errorMessage The error message to display
     * @param userName The user name
     */
    private askCredentials(_?, errorMessage?: string, userName?: string) {
        this.getCredentialsCallback(errorMessage, userName);
    }
}

export default class DriveApi {
    private api: BaseGeotabApi;
    private lastDatabaseExistsCall: Date;
    private credentialsStore = new DriveCredentialsStore(this.store);

    constructor(
        protected readonly userService: UserService,
        protected readonly store: DriveStore,
        userName?: string,
        protected readonly featureFlagService?: IFeatureFlagsService,
        private readonly contextSource?: TContextSource
    ) {
        this.lastDatabaseExistsCall = new Date(0);
        this.refresh(userName);
    }

    authenticate(newServer: string, database: string, username: string, password: string, success: TApiSuccessCallback, error: TApiErrorCallback) {
        return this.api.authenticate(newServer, database, username, password, success, error);
    }

    call(
        method: string,
        params: any,
        callbackSuccess?:
        TApiSuccessCallback,
        callbackError?: TApiErrorCallback,
        ignoreCredentials?: boolean,
        binaryData?: ArrayBuffer,
        binaryMimeType?: string
    ) {
        this.api.call(method, params, callbackSuccess || NOOP, (errorObject) => {
            this.handleInternalErrors(errorObject, callbackError, method);
        }, ignoreCredentials, binaryData, binaryMimeType);
    }

    callPromise(method: string, params: any, ignoreCredentials?: boolean) {
        return new Promise((resolve, reject) => {
            this.call(method, params, resolve, reject, ignoreCredentials);
        });
    }

    multiCall(
        calls: any[],
        callbackSuccess: TApiSuccessCallback,
        callbackError: TApiErrorCallback,
        ignoreCredentials = false
    ) {
        this.api.multiCall(calls, callbackSuccess, (errorObject) => {
            this.handleInternalErrors(errorObject, callbackError);
        }, ignoreCredentials);
    }

    multiCallPromise(calls: any[], ignoreCredentials = false): Promise {
        return new Promise<any[]>((resolve, reject: (error: IApiError) => void) => {
            this.multiCall(calls, resolve, reject, ignoreCredentials);
        });
    }

    // NOTE : param successCallback is never used by our internal code, might be used by 3rd party code.
    getSession(successCallback: TApiCredentialsCallback | null, newSession = false, ignoreApiNeedsCredentials = false) {
        return this.api.getSession(newSession, ignoreApiNeedsCredentials)
        .then(credentialsWithServer => {
                successCallback && successCallback(credentialsWithServer.credentials, credentialsWithServer.server, credentialsWithServer.iamSettings);
                return credentialsWithServer;
            });
    }

    forget(successCallback?: TApiSuccessCallback, errorMessage?: string, skipNeedsCredentials = false) {
        this.api.forget(successCallback, errorMessage, undefined, skipNeedsCredentials);
    }

    refresh(userName?: string | boolean) {
        let apiOptions = {
            timeout: 5 * 60, // Default timeout is 5 minutes for a call
            disableSsl: !!(window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_SSL)
        };
        let sanitizedUserName = userName;

        if (userName && typeof (userName) === "string") {
            sanitizedUserName = sanitizeUserName(userName);
            this.credentialsStore.userName = sanitizedUserName;
        } else {
            this.credentialsStore = new DriveCredentialsStore(this.store);
        }
        this.api = new BaseGeotabApi((errorMessage, username) => {
            dispatcher.trigger(DispatcherEvents.ApiNeedsCredentials, sanitizedUserName || username, errorMessage);
        }, apiOptions, this.credentialsStore, undefined, this.store);

        this.api.onChangeNetworkStatus((isOnline) => {
            dispatcher.trigger(DispatcherEvents.StatusOnline, isOnline);
        });

        if (!this.getCurrentCredentials()) {
            // If we're creating this API for a tempContext, we don't want to trigger the ApiNeedsCredentials event because it is confusing for users.
            let ignoreApiNeedsCredentials = this.contextSource && this.contextSource === "getTempContext";
            void this.getSession(null, true, ignoreApiNeedsCredentials)
                .then(() => log(Source.SERVICES, "Initialized API session"));
        }
    }

    setUser(credentials: ICredentials, server: string, iamSettings?: IIamSettings) {
        this.credentialsStore.set(credentials, server, iamSettings);
        this.api.refreshSession();
    }

    getUser(): IUser | null {
        let users = this.userService.getUsersObject();
        return users[this.credentialsStore.userName] as IUser || null;
    }

    getCurrentCredentials() {
        return this.credentialsStore.get();
    }

    getDriverCredentials(username: string) {
        return this.credentialsStore.get(username);
    }

    getXHRError(xhr: XMLHttpRequest) {
        return this.api.getXHRError(xhr);
    }

    databaseExists(credentials: ICredentials): Promise<boolean> {
        return this.callPromise("DatabaseExists", {
            dbName: credentials.database
        });
    }

    setTransport(transport: Transport) {
        this.api.setTransport(transport);
    }

    setHeaders(headers: ICustomHeaders[]) {
        this.api.setHeaders(headers);
    }

    destroy() {
        this.api.destroy();
    }

    private canCallDatabaseExists(): boolean {
        return getServerDate().getTime() >= (this.lastDatabaseExistsCall.getTime() + DATABASEEXISTS_INTERVAL_MS);
    }

    private handleInternalErrors(error: IApiError, callback?: (error: IApiError) => void, method?: string) {
        if (method && method === "DatabaseExists") {
            // Don't repeatedly try to call DatabaseExists if it fails to return a response
            callback && callback(error);
            return;
        }
        if (error && error.data && error.data.type === "DbUnavailableException" && this.getCurrentCredentials() && this.canCallDatabaseExists()) {
            let credentials = this.getCurrentCredentials();

            if (credentials && credentials.credentials && credentials.credentials.database) {
                let creds = credentials;
                this.lastDatabaseExistsCall = getServerDate();

                this.databaseExists(creds.credentials).then(exists => {
                    log(Source.SERVICES, `Got '${ error.data.type }'; ${ exists ? "does" : "doesn't" } exist in fed`);
                    if (!exists) {
                        logger.log(logger.Source.API, `Database '${ creds.credentials.database }' does not exist`);
                        this.forget();
                        void this.getSession(null, true);
                    }
                    callback && callback(error);
                }).catch((e: IApiError) => {
                    const errorMessage = e && e.message ? ` Error: ${ e.message }` : "";
                    logger.error(logger.Source.API, `Unable to get database detail for database ${ creds.credentials.database }.${ errorMessage }`, this.getUser(), e);
                    callback && callback(error);
                });
            }
        } else {
            callback && callback(error);
        }
    }
}
